import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest, distinctUntilChanged, filter, map, tap } from 'rxjs';

import { StateService } from '@ninety/platform/services';
import {
  FeatureFlagFacade,
  FeatureFlagKeys,
  selectIsBillingV3Company,
  appActions,
  selectCurrentRoute,
} from '@ninety/platform/state';
import { selectHasTier1Data } from '@ninety/platform/state/app-init/app-init.selectors';
import { NotificationBannerComponent } from '@ninety/ui/core';
import { BillingNotificationBannerComponent } from '@ninety/web/pages/billing-v3/components/billing-notification-banner.component/billing-notification-banner.component';
import { ManagerPermissionChangesBannerComponent } from '@ninety/web/pages/billing-v3/components/manager-permission-changes-banner.component/manager-permission-changes-banner.component';
import { SubscriptionRenewalBannerComponent } from '@ninety/web/pages/billing-v3/components/subscription-renewal-banner.component/subscription-renewal-banner.component';
import { ConversationSidenavComponent } from '@ninety/web/pages/feedback/conversation-sidenav/conversation-sidenav.component';
import { MeetingService } from '@ninety/web/pages/meeting/_shared/services/meeting.service';
import { MeetingNotesComponent } from '@ninety/web/pages/meeting/meeting-notes/meeting-notes.component';
import { MeetingSidenavV2Component } from '@ninety/web/pages/meeting/meeting-sidenav-v2/meeting-sidenav-v2.component';

import { NinetyLayoutType } from '../../_state/page/page-state.models';
import { selectLayoutType } from '../../_state/page/page-state.selectors';
import { MainLayoutComponent } from '../../main-layout/main-layout.component';
import { MainSidebarComponent } from '../../main-layout/main-sidebar/main-sidebar.component';

@Component({
  selector: 'ninety-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [
    MatProgressSpinner,
    MainLayoutComponent,
    NotificationBannerComponent,
    BillingNotificationBannerComponent,
    MeetingSidenavV2Component,
    ConversationSidenavComponent,
    MainSidebarComponent,
    RouterOutlet,
    MeetingNotesComponent,
    AsyncPipe,
    PushPipe,
    SubscriptionRenewalBannerComponent,
    ManagerPermissionChangesBannerComponent,
  ],
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly stateService = inject(StateService);
  private readonly meetingService = inject(MeetingService);
  private readonly featureFlagFacade = inject(FeatureFlagFacade);
  private readonly store = inject(Store);

  public state$!: Observable<{
    inMeeting: boolean;
    inFeedback: boolean;
    isMobile: boolean;
    isTier1DataLoaded: boolean;
    layoutType: NinetyLayoutType;
    showNotes: boolean;
    showQuarterlyQuestions: boolean;
    showSideNav: boolean;
  }>;
  public layouts = NinetyLayoutType;

  private readonly hasTier1DataLoaded$ = this.store.select(selectHasTier1Data);
  private readonly layoutType$ = this.store.select(selectLayoutType);
  private readonly route$ = this.store.select(selectCurrentRoute);

  protected readonly billingV3Company$ = this.store.select(selectIsBillingV3Company);

  private readonly subscriptions = new Subscription();

  public ngOnInit(): void {
    this.state$ = combineLatest({
      inMeeting: this.layoutType$.pipe(
        map(layout => layout !== this.layouts.normal),
        distinctUntilChanged()
      ),
      inFeedback: combineLatest([
        this.route$,
        this.featureFlagFacade.getFlag(FeatureFlagKeys.webWeeklyConversations),
      ]).pipe(map(([route]) => route?.data.route === 'feedback')),
      isMobile: this.breakpointObserver.observe(['(min-width: 960px)']).pipe(
        map(({ breakpoints }) => !breakpoints['(min-width: 960px)']),
        tap(isMobile => this.stateService.toggleMeetingSideNav(!isMobile))
      ),
      isTier1DataLoaded: this.hasTier1DataLoaded$,
      layoutType: this.layoutType$,
      showNotes: this.meetingService.showNotes$,
      showQuarterlyQuestions: this.featureFlagFacade.getFlag(FeatureFlagKeys.quarterlyQuestionSettings),
      showSideNav: this.stateService.toggleMeetingSideNavB$,
    });

    this.subscriptions.add(
      this.hasTier1DataLoaded$
        .pipe(
          filter(loaded => loaded),
          distinctUntilChanged()
        )
        .subscribe(() => {
          this.store.dispatch(appActions.dispatchAppStartActions());
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
